import { Heading, Link } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { CloseIcon, SyncIcon } from '@mm/icons';
import { StoreState } from '@mm/state/store-interfaces';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { useViewActions } from '@mm/state/actions/viewActions';
import React, { useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import styles from './ZapierIntegrationSlideoutEditor.module.css';
import Input from '../../../../../common/components/Input/Input';
import { GreenMarker } from '../../../../../common/components/GreenMarker/GreenMarker';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useApiKeys } from '../../../../features/automation/common/use-api-keys';
import { useGreenCueMarkers } from '../../../../../common/hooks/use-green-cue-markers';
import PrimarySelect from '../../../../../common/components/PrimarySelect/PrimarySelect';
import { useConnectionWidgetHandlers } from '../../../../../common/helpers/widget-helpers/use-connection-widget-handlers';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';

export type ZapierIntegrationSlideoutProps = {
  widgetId: number;
};

export const ZapierIntegrationSlideoutEditor = (
  props: ZapierIntegrationSlideoutProps,
) => {
  const { widgetId } = props;
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === widgetId)!;
  const connectionWidgetHandlers = useConnectionWidgetHandlers();
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const { openUniversalUpgradeLandmineModal } = useModalActions();
  const { retrieveWidgetConnections } = useConnectionCRUDActions();
  const { isZapierActivated } = useGreenCueMarkers();
  const viewActions = useViewActions();
  const { setRef } = useSlideoutOnClose();
  const { apiKey, regenerateKey, isUpgradeRequired } = useApiKeys(
    'Zapier',
    'Customers.ai API Key',
    {
      headerText: '\u26a0 Regenerate Customers.ai API Key',
      okButtonText: 'Regenerate Key',
      messageText: (
        <>
          <p>All API requests using this key will need to be updated.</p>
          <p>This step cannot be undone.</p>
        </>
      ),
    },
  );

  const [copyActionCompleted, setCopyActionCompleted] = useState(false);
  const [apiKeyInputRef, setApiKeyInputRef] = useState<
    HTMLInputElement | undefined
  >(undefined);
  const openUpgradeModal = (isNoLongerAvailable = false) => {
    const landmineName = isNoLongerAvailable
      ? `${landmineNames.FEATURE_NO_LONGER_AVAILABLE}_${landmineNames.INTEGRATIONS_ZAPIER}`
      : landmineNames.INTEGRATIONS_ZAPIER;

    openUniversalUpgradeLandmineModal({
      landmineName,
      feature: 'zapier',
    });
  };
  const selectApiKeyText = () => {
    if (isUpgradeRequired) {
      openUpgradeModal();
    }
    setTimeout(() => {
      if (apiKeyInputRef) {
        apiKeyInputRef.setSelectionRange(0, apiKeyInputRef.value.length);
      }
    }, 10);
  };
  const copyIconClassName = classNames('fa-copy', {
    far: true,
  });

  useEffect(() => {
    if (activeFbPageId) {
      retrieveWidgetConnections(activeFbPageId);
    }
  }, [activeFbPageId]);

  const connections =
    useSelector((state: StoreState) => state.connections.widgetConnections) ||
    [];
  const connectionsToShow = connections.filter(
    c => !!c.sendDataWidgetConfig.resourceSubscriptionTriggerName,
  );

  const [localWidget, setLocalWidget] = useState(widget);
  const { resourceSubscriptionTriggerName: zapName } = localWidget.config;
  const selectedConnection =
    connectionsToShow && connectionsToShow.find(c => c.name === zapName);

  const onConnectionChange = (connection: ConnectionModel) => {
    const { sendDataWidgetConfig: config } = connection;
    if (config) {
      const clonedWidget = {
        ...widget,
        config: {
          ...config,
          kind: 'zapier' as any,
        },
      };
      setLocalWidget(clonedWidget);
      connectionWidgetHandlers.update(clonedWidget);
    }
  };

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <div className={styles.headerContent}>
        <Heading className={styles.headerTitle} size={3}>
          Zapier Integration
        </Heading>
        <div className={styles.headerButtons}>
          <div
            onClick={viewActions.hideSlideoutMenu}
            className={styles.closeIcon}
          >
            <CloseIcon color="#8c8c8c" />
          </div>
        </div>
      </div>
      <SlideoutMenu.Content className={styles.content}>
        <div className={styles.configArea}>
          <div className={styles.copyKeyContainer}>
            <div className={styles.header}>Copy your API Key</div>
            <div className={styles.title}>
              API Key
              {copyActionCompleted && (
                <span className={styles.copiedText}>
                  <i className="fas fa-check" /> Copied!
                </span>
              )}
            </div>
            <div className={styles.zapierPanel}>
              <div className={styles.zapierApiKeyRow}>
                <Input
                  inputRef={setApiKeyInputRef}
                  className={styles.zapierApiKeyInput}
                  readOnly="readonly"
                  value={
                    isUpgradeRequired
                      ? 'Upgrade to unlock this feature!'
                      : (apiKey && apiKey.authToken) || ''
                  }
                  onFocus={selectApiKeyText}
                />
                <CopyToClipboard
                  text={(apiKey && apiKey.authToken) || ''}
                  onCopy={() => setCopyActionCompleted(true)}
                >
                  <PrimaryButton
                    className={styles.copyBtn}
                    color={ButtonColor.gray}
                    disabled={isUpgradeRequired}
                    leftIcon={<i className={copyIconClassName} />}
                  >
                    Copy to clipboard
                  </PrimaryButton>
                </CopyToClipboard>
                {!isZapierActivated && (
                  <GreenMarker className="zapier-panel__copy-cue" />
                )}
              </div>
              <div className={styles.description}>
                Copy this key to your clipboard! You'll need it to connect your
                Zapier account to MobileMonkey for your Facebook Page
              </div>
              <PrimaryButton
                className={styles.regenerateBtn}
                onClick={regenerateKey}
                disabled={isUpgradeRequired}
                leftIcon={<SyncIcon />}
                color={ButtonColor.transparent}
              >
                Regenerate Key
              </PrimaryButton>
            </div>
          </div>
          <div className={styles.chooseConnection}>
            <div className={styles.header}>Choose Zapier Connection</div>
            <div className={styles.connectionPanel}>
              <PrimarySelect
                className={styles.connectionSelect}
                onChange={onConnectionChange}
                value={selectedConnection}
                options={connectionsToShow}
                showSearchFilter={true}
                closeOnSearchRightSideClick={true}
                customDropdownStyles={{
                  zIndex: '1000003',
                }}
              />

              <p className={styles.templateDescription}>
                Create your own Zap template{' '}
                <Link
                  className={styles.link}
                  href="https://zapier.com/apps/mobilemonkey/integrations"
                  isBlank
                  capitalize={false}
                  rel="noopener noreferrer"
                >
                  here
                </Link>
                !
              </p>
            </div>
          </div>
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
